<script setup lang="ts">
import TextField from '@/components/forms/TextField/TextField.vue';
import { isEqual } from 'lodash';
import { computed, ref, watch } from 'vue';
import type { NumberFieldProps } from '../types';
import {
  NumberDisplayOptions,
  formatNumber,
  getDecimalSeparator,
  parseNumber,
} from '@/utils/numberUtils';
import { escapeForRegex } from '@/utils/stringUtils';
import { useAccountStore } from '@/stores/accountStore';

const props = withDefaults(defineProps<NumberFieldProps>(), {
  mode: 'edit',
});

const accountStore = useAccountStore();

const decimalSeparator = computed(() => getDecimalSeparator(accountStore.language));

const passdownProps = computed(() => ({
  ...props,
  emptyValue: '',
}));

const targetFormat = computed<NumberDisplayOptions>(() => ({
  ...props.format,
}));

const emits = defineEmits<{
  'update:modelValue': [number | undefined];
}>();

const isValidNumber = (num: number | undefined): boolean => num !== undefined && !isNaN(num);

const internalValue = ref(formatNumberWrapper(props.modelValue));

function formatNumberWrapper(value?: number) {
  // only do rounding and decimal-decisions when in mode "details"
  const format =
    props.mode === 'details' ? targetFormat.value : { scientific: targetFormat.value.scientific };

  return formatNumber(value, format);
}

// we need an external value, to make sure that the input-value is only formatted on blur
const externalValue = ref<number | undefined>(props.modelValue);

function setValues(newValue: number | undefined) {
  if (String(newValue) !== String(externalValue.value)) {
    internalValue.value = formatNumberWrapper(newValue);
    externalValue.value = newValue;
  }
}

watch(
  () => props.modelValue,
  (newValue) => {
    setValues(newValue);
  },
  { deep: true },
);

watch(
  () => targetFormat.value,
  (newValue, oldValue) => {
    setValues(props.modelValue);
    if (!isEqual(newValue, oldValue)) {
      onUpdate(internalValue.value);
      onBlur();
    }
  },
);

function onUpdate(input: string) {
  internalValue.value = input;
  const parsedValue = parseNumber(input) ?? props.emptyValue; // could be NaN

  externalValue.value = parsedValue;

  emits('update:modelValue', parsedValue);
}

function onBlur() {
  if (isValidNumber(externalValue.value)) {
    internalValue.value = formatNumberWrapper(externalValue.value);
  }
}

function onBeforeinput(e: InputEvent) {
  if (!e.data) return;
  const inputElement = e.target as HTMLInputElement;
  const { value: existingTxt, selectionStart, selectionEnd } = inputElement ?? {};

  const potentialNewInputVal = existingTxt
    ? existingTxt.slice(0, selectionStart as number | undefined) +
      e.data +
      existingTxt.slice(selectionEnd as number | undefined)
    : e.data;

  // only allow valid numerical inputs but do not check to much (e.g. count decimalSeparators)
  const allowedDecimalSeparator =
    targetFormat.value.valueType === 'FloatingPoint' && decimalSeparator.value
      ? escapeForRegex(decimalSeparator.value)
      : '';

  const regex = `^[0-9eE${allowedDecimalSeparator}+-]*$`;
  let valid = new RegExp(regex).test(potentialNewInputVal);

  // Additionally, ensure IEEE 754 compliance for the exponent: it must be an integer.
  // If there is an exponent marker (e/E), reject inputs that contain a decimal separator in the exponent part.
  // This is to prevent the user from entering "1e-2.5" or "1e+0.5" (which would be interpreted as 1e-2 or 1e+0).
  if (valid) {
    const lastLower = potentialNewInputVal.lastIndexOf('e');
    const lastUpper = potentialNewInputVal.lastIndexOf('E');
    const lastExpIdx = Math.max(lastLower, lastUpper);
    // Only check when decimal separators are allowed at all (i.e., non-integer valueType)
    if (lastExpIdx !== -1 && decimalSeparator.value) {
      const expPart = potentialNewInputVal.slice(lastExpIdx + 1);
      if (expPart.includes(decimalSeparator.value)) {
        valid = false;
      }
    }
  }

  if (!valid) {
    e.preventDefault();
  }
}
</script>

<template>
  <TextField
    class="number-field"
    v-bind="passdownProps"
    :modelValue="internalValue"
    @update:modelValue="onUpdate"
    @blur="onBlur"
    @beforeinput="onBeforeinput"
  />
</template>

<style lang="scss" scoped>
:deep(.v-field__input) {
  text-align: right;
}
:deep(.display) {
  text-align: right;
}
</style>
